import {
  Avatar,
  AvatarProps,
  Skeleton,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd'

import { strToHexColor } from '@/utils/colors'

const Text = Typography.Text
const FALLBACK_NAME = 'Unknown'

type UserAvatarProps = {
  user?: boolean
  avatar: string
  email?: string
  isLoading?: boolean
  name?: string
  noTooltip?: boolean
  role?: string
  size?: 'small' | 'large' | number | undefined
  testId?: string
} & AvatarProps

export function UserAvatar({
  user,
  avatar,
  email,
  isLoading = false,
  name,
  noTooltip,
  role,
  size,
  testId,
  ...rest
}: UserAvatarProps): JSX.Element {
  if (isLoading)
    return <Skeleton.Avatar active={true} size={size} shape={'circle'} />

  const avatarName = name || email || FALLBACK_NAME
  const src = addDefault404ToGravatar(avatar)
  const initials = getInitials(avatarName)
  const backgroundColor = src?.includes('gravatar')
    ? strToHexColor(avatarName)
    : null

  const fontColor =
    document.getElementsByClassName('cl-light-theme').length > 0
      ? 'var(--component-background)'
      : 'var(--component-background-dark)'

  const avatarComponent = (
    <Avatar
      shape={rest.shape}
      data-cy={testId ? `Avatar-${testId}` : `Avatar-${name}`}
      size={size}
      src={src}
      style={{
        backgroundColor,
        marginRight: user ? '15px' : '0',
        color: fontColor,
      }}
    >
      {initials}
    </Avatar>
  )
  const overlay = noTooltip ? null : (
    <Space direction="vertical">
      {avatarName && <Text>{avatarName}</Text>}
      {email && <Text copyable>{email}</Text>}
      {role && <Tag color="purple">{role}</Tag>}
    </Space>
  )
  return <Tooltip overlay={overlay}>{avatarComponent}</Tooltip>
}

export function addDefault404ToGravatar(avatar: string): string {
  return avatar?.includes('gravatar.com') ? `${avatar}&d=404` : avatar
}

export function getInitials(name: string) {
  if (!name) return name

  const trimmedName = name.trim()

  if (trimmedName.length <= 3) return trimmedName

  return trimmedName
    .split(/\s+/)
    .map((w) => [...w][0])
    .slice(0, 3)
    .join('')
}
